import { WishlistUpdateEvent } from '@theme/events';

/**
 * @typedef {object} WishlistItem
 * @property {string} id - The product id.
 * @property {string} handle - The product handle.
 * @property {string} url - The product url.
 * @property {string} addedAt - ISO timestamp of when the item was saved.
 */

/**
 * Client-side, localStorage-backed wishlist. Stores only enough to identify
 * a product (id/handle/url) — title/image/price are always re-fetched live
 * from the product's `.js` endpoint when rendering, so displayed data never
 * goes stale.
 */
export class Wishlist {
  static #STORAGE_KEY = 'pf-wishlist-v1';
  static #NOTES_KEY = 'pf-wishlist-notes-v1';
  static #MAX_ITEMS = 200;

  /**
   * @returns {WishlistItem[]}
   */
  static getItems() {
    try {
      const items = JSON.parse(localStorage.getItem(this.#STORAGE_KEY) ?? '[]');
      return Array.isArray(items) ? items : [];
    } catch (_) {
      return [];
    }
  }

  /**
   * @param {string | number} productId
   * @returns {boolean}
   */
  static has(productId) {
    const id = String(productId);
    return this.getItems().some((item) => item.id === id);
  }

  /**
   * @returns {number}
   */
  static count() {
    return this.getItems().length;
  }

  /**
   * @param {{ id: string | number, handle: string, url: string }} item
   */
  static add(item) {
    const id = String(item.id);
    const items = this.getItems().filter((i) => i.id !== id);
    items.unshift({ id, handle: item.handle, url: item.url, addedAt: new Date().toISOString() });
    this.#persist(items.slice(0, this.#MAX_ITEMS));
    this.#dispatch('add', id);
  }

  /**
   * @param {string | number} productId
   */
  static remove(productId) {
    const id = String(productId);
    this.#persist(this.getItems().filter((i) => i.id !== id));
    this.#removeNote(id);
    this.#dispatch('remove', id);
  }

  /**
   * Adds the item if not saved, removes it if already saved.
   * @param {{ id: string | number, handle: string, url: string }} item
   * @returns {boolean} Whether the item is now saved.
   */
  static toggle(item) {
    const saved = this.has(item.id);
    if (saved) {
      this.remove(item.id);
    } else {
      this.add(item);
    }
    return !saved;
  }

  /**
   * @param {string | number} productId
   * @returns {string}
   */
  static getNote(productId) {
    return this.#getNotes()[String(productId)] ?? '';
  }

  /**
   * @param {string | number} productId
   * @param {string} text
   */
  static setNote(productId, text) {
    const id = String(productId);
    const notes = this.#getNotes();

    if (text) {
      notes[id] = text;
    } else {
      delete notes[id];
    }

    try {
      localStorage.setItem(this.#NOTES_KEY, JSON.stringify(notes));
    } catch (_) {
      // Quota exceeded or private-mode storage — non-fatal, note simply won't persist.
    }

    this.#dispatch('note', id);
  }

  static clear() {
    try {
      localStorage.removeItem(this.#STORAGE_KEY);
      localStorage.removeItem(this.#NOTES_KEY);
    } catch (_) {
      // no-op
    }
    this.#dispatch('clear', null);
  }

  /**
   * @param {string} productId
   */
  static #removeNote(productId) {
    const notes = this.#getNotes();
    delete notes[productId];
    try {
      localStorage.setItem(this.#NOTES_KEY, JSON.stringify(notes));
    } catch (_) {
      // no-op
    }
  }

  /**
   * @returns {Record<string, string>}
   */
  static #getNotes() {
    try {
      const notes = JSON.parse(localStorage.getItem(this.#NOTES_KEY) ?? '{}');
      return notes && typeof notes === 'object' ? notes : {};
    } catch (_) {
      return {};
    }
  }

  /**
   * @param {WishlistItem[]} items
   */
  static #persist(items) {
    try {
      localStorage.setItem(this.#STORAGE_KEY, JSON.stringify(items));
    } catch (_) {
      // Quota exceeded or private-mode storage — non-fatal, wishlist simply won't persist.
    }
  }

  /**
   * @param {'add' | 'remove' | 'note' | 'clear'} action
   * @param {string | null} productId
   */
  static #dispatch(action, productId) {
    document.dispatchEvent(
      new WishlistUpdateEvent(this.getItems(), productId, {
        itemCount: this.count(),
        productId,
        action,
      })
    );
  }
}
